import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Button, Input, TextArea, Checkbox, FormField, Alert } from '../ui';
import { GlossaryEntry } from '../../types/glossary';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface GlossaryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialEntry?: GlossaryEntry | null;
  isEditMode?: boolean;
}

const GlossaryForm: React.FC<GlossaryFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialEntry = null,
  isEditMode = false,
}) => {
  const [word, setWord] = useState('');
  const [definition, setDefinition] = useState('');
  const [example, setExample] = useState('');
  const [source, setSource] = useState('');
  const [isLearned, setIsLearned] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialEntry) {
        setWord(initialEntry.word || '');
        setDefinition(initialEntry.definition || '');
        setExample(initialEntry.example || '');
        setSource(initialEntry.source || '');
        setIsLearned(initialEntry.is_learned || false);
        setTags(initialEntry.tags || []);
      } else {
        resetForm();
      }
    }
  }, [isOpen, isEditMode, initialEntry]);

  const resetForm = () => {
    setWord('');
    setDefinition('');
    setExample('');
    setSource('');
    setIsLearned(false);
    setTags([]);
    setTagInput('');
    setError(null);
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (newTag && !tags.includes(newTag)) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!word.trim()) {
      setError('Word is required');
      setIsSubmitting(false);
      return;
    }

    const payload = {
      word: word.trim(),
      definition: definition.trim(),
      example: example.trim(),
      source: source.trim(),
      is_learned: isLearned,
      tags: tags,
    };

    try {
      if (isEditMode && initialEntry) {
        await apiClient.put(`/api/glossary/${initialEntry.id}/`, payload);
      } else {
        await apiClient.post('/api/glossary/', payload);
      }
      onSuccess();
    } catch (err: any) {
      console.error('Error saving glossary entry:', err);
      setError(err.response?.data?.detail || 'Failed to save entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size['2xl'],
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                    {isEditMode ? 'Edit Glossary Entry' : 'Add New Word'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: COLORS.text.tertiary,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={onClose}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  <FormField label="Word / Concept" htmlFor="word" required>
                    <Input
                      type="text"
                      id="word"
                      value={word}
                      onChange={(e) => setWord(e.target.value)}
                      placeholder="e.g., Serendipity"
                      required
                      autoFocus
                    />
                  </FormField>

                  <FormField label="Definition" htmlFor="definition">
                    <TextArea
                      id="definition"
                      value={definition}
                      onChange={(e) => setDefinition(e.target.value)}
                      placeholder="What does it mean?"
                      rows={3}
                    />
                  </FormField>

                  <FormField label="Example" htmlFor="example">
                    <TextArea
                      id="example"
                      value={example}
                      onChange={(e) => setExample(e.target.value)}
                      placeholder="How is it used?"
                      rows={2}
                    />
                  </FormField>

                  <FormField label="Source" htmlFor="source">
                    <Input
                      type="text"
                      id="source"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      placeholder="Where did you encounter this?"
                    />
                  </FormField>

                  <FormField label="Tags (Optional)" htmlFor="tags">
                    <Input
                      type="text"
                      id="tags"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleAddTag}
                      placeholder="Type and press Enter or comma to add tags..."
                    />
                    <p style={{
                      marginTop: SPACING[4],
                      fontSize: TYPOGRAPHY.size.xs,
                      color: COLORS.text.secondary,
                    }}>
                      Press Enter or comma after each tag
                    </p>
                    {tags.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: SPACING[8],
                        marginTop: SPACING[8],
                      }}>
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: SPACING[4],
                              paddingLeft: SPACING[8],
                              paddingRight: SPACING[8],
                              paddingTop: SPACING[4],
                              paddingBottom: SPACING[4],
                              backgroundColor: '#DBEAFE',
                              color: '#1E40AF',
                              fontSize: TYPOGRAPHY.size.sm,
                              borderRadius: RADIUS,
                            }}
                          >
                            #{tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'inherit',
                                padding: 0,
                              }}
                            >
                              <XCircleIcon style={{ height: '16px', width: '16px' }} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </FormField>

                  <FormField label="Status">
                    <Checkbox
                      id="is_learned"
                      label="Mark as learned/mastered"
                      checked={isLearned}
                      onChange={(e) => setIsLearned(e.target.checked)}
                    />
                  </FormField>

                  {error && (
                    <Alert variant="error">
                      {error}
                    </Alert>
                  )}

                  <div style={{
                    marginTop: SPACING[24],
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: SPACING[12],
                  }}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={isSubmitting}
                      loadingText={isEditMode ? 'Updating...' : 'Adding...'}
                    >
                      {isEditMode ? 'Update' : 'Add Word'}
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GlossaryForm;
